import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  useRef
} from "react"
import type { ReactNode } from "react"
import { createPortal } from "react-dom"
import useRerenderOnce from "../use-rerender-once"

const canUseDOM = () => typeof window !== "undefined" && window.document

export const StackingContext = createContext<HTMLElement | null>(null)

export type PortalNodeProps = {
  /** Content rendered inside the portal node */
  children?: ReactNode
  /** CSS class applied to the portal node container */
  className?: string
}

/**
 * Creates a stacking context that child Portal components will render into
 * instead of document.body.
 */
export function PortalNode({ children, className }: PortalNodeProps) {
  useRerenderOnce()
  const node = useRef<HTMLDivElement>(null)

  return (
    <StackingContext.Provider value={node.current}>
      <div className={className} ref={node}>
        {" "}
        {children}
      </div>
    </StackingContext.Provider>
  )
}

// Each Portal creates a new StackingContext, currently with no way to opt-out
// of this behavior. This means that anything rendered inside a nested portal
// will use the root of the previous portal.
function useRootNode() {
  return useContext(StackingContext) || document.body
}

function DOMPortal({ children }: { children?: ReactNode }) {
  const rootNode = useRootNode()
  const [node] = useState(() => document.createElement("div"))

  useEffect(() => {
    rootNode.appendChild(node)

    return () => {
      node.remove()
    }
  }, [rootNode, node])

  return (
    <StackingContext.Provider value={node}>
      {createPortal(children, node)}
    </StackingContext.Provider>
  )
}

function ServerPortal() {
  return null
}

/**
 * Renders children into a DOM node outside the parent component hierarchy.
 * Uses React portals under the hood. On the server, renders nothing.
 * Supports nested stacking contexts via PortalNode.
 */
function Portal({ children }: { children?: ReactNode }) {
  return canUseDOM() ? <DOMPortal>{children}</DOMPortal> : <ServerPortal />
}

export { Portal }
export default Portal
